import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Box, Paper, Typography, Divider, Table, TableHead, TableRow, TableCell, TableBody, Button } from "@mui/material";

interface ReceiptLine {
  feeHead: { name: string };
  feeInstallment: { name: string };
  amount: number;
}

interface Receipt {
  receiptNo: string;
  paymentDate: string;
  totalPaid: number;
  school: { name: string };
  student: { admissionNumber: string; firstName: string; lastName: string; class?: { name: string }; section?: { name: string } };
  paymentMode?: { name: string };
  lines: ReceiptLine[];
}

const ReceiptView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Receipt | null>(null);

  useEffect(() => {
    (async () => {
      if (!id) return;
      const res = await axios.get(`/fee-collection/receipt/${id}`);
      setData(res.data);
    })();
  }, [id]);

  const onPrint = () => window.print();

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 900, margin: "0 auto" }}>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography variant="h6">{data?.school?.name || "School"}</Typography>
          <Typography variant="subtitle2">Fee Receipt</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography>Receipt No: {data?.receiptNo}</Typography>
            <Typography>Date: {data ? new Date(data.paymentDate).toLocaleDateString() : ""}</Typography>
          </Box>
          <Box>
            <Typography>Mode: {data?.paymentMode?.name}</Typography>
            <Typography>Total Paid: {data?.totalPaid?.toFixed(2)}</Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <Typography>Admission No: {data?.student?.admissionNumber}</Typography>
          <Typography>Student: {data?.student?.firstName} {data?.student?.lastName}</Typography>
          <Typography>Class/Section: {data?.student?.class?.name} {data?.student?.section?.name}</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fee Head</TableCell>
              <TableCell>Installment</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.lines?.map((l, idx) => (
              <TableRow key={idx}>
                <TableCell>{l.feeHead?.name}</TableCell>
                <TableCell>{l.feeInstallment?.name}</TableCell>
                <TableCell align="right">{Number(l.amount).toFixed(2)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell />
              <TableCell align="right"><strong>Total</strong></TableCell>
              <TableCell align="right"><strong>{data?.totalPaid?.toFixed(2)}</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Button variant="contained" onClick={onPrint}>Print</Button>
        </Box>
      </Paper>
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; }
          .MuiPaper-root { box-shadow: none !important; }
          @page { size: A4; margin: 20mm; }
          button { display: none; }
        }
      `}</style>
    </Box>
  );
};

export default ReceiptView;
